import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { AuthKitProvider, useAuth } from '@workos-inc/authkit-react';
import {
  readDevIdentity,
  setAuthTokenGetter,
  writeDevIdentity,
  type AuthTokenGetter,
} from './api.ts';

/**
 * WorkOS AuthKit, wired the way `app/insights` does it.
 *
 * The structure is copied deliberately: AuthKit is mounted at the root, a thin wrapper
 * pulls `getAccessToken` out of it, and that function is handed to the fetch layer
 * through a module-level setter rather than threaded through every call site. AuthKit
 * refreshes the token behind that function, so asking it per request is what keeps a
 * long session working — caching the string would break at the first refresh.
 *
 * `devMode` is not set here. It auto-enables on localhost, and setting it explicitly in
 * production would keep refresh tokens in `localStorage`; the custom authentication
 * domain is what turns it off properly.
 */

export interface PortalAuth {
  /** False while AuthKit is still resolving a session from the URL or storage. */
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Changes whenever the authenticated identity changes, so cached viewer data cannot leak. */
  sessionKey: string;
  /** Starts WorkOS sign-in, or selects the default automated test user. */
  signIn: (() => void) | null;
  /** Clears the current provider session so a different account can be chosen. */
  switchAccount: (() => void) | null;
  /** Shortcut for automated test auth. */
  signInAsAdmin: (() => void) | null;
  signOut: (() => void) | null;
  email: string | null;
}

const PortalAuthContext = createContext<PortalAuth | undefined>(undefined);

/** Never equal to a real token getter, so the first render always registers one. */
const UNREGISTERED = Symbol('unregistered');

const CLIENT_ID = import.meta.env['VITE_WORK_OS_APP_CLIENT_ID'];
const AUTH_API_HOSTNAME = import.meta.env['VITE_WORK_OS_AUTH_API_HOSTNAME'];

/**
 * The session AuthKit has, translated into what the portal needs.
 *
 * Only mounted when a client id is configured — otherwise `useAuth` has no provider
 * above it and throws.
 */
function AuthKitBridge({ children }: PropsWithChildren) {
  const auth = useAuth();
  const { user, getAccessToken, signIn, signOut } = auth;
  const sessionKey = user ? `workos:${user.email}` : 'workos:none';

  const signOutToHome = useCallback(() => {
    const done = () => window.location.assign('/');
    try {
      void Promise.resolve(signOut({ returnTo: window.location.origin, navigate: false })).finally(
        done,
      );
    } catch {
      done();
    }
  }, [signOut]);

  const tokenGetter = useMemo<AuthTokenGetter | null>(
    () =>
      user
        ? () =>
            getAccessToken().catch(() => {
              // A refresh that fails is a signed-out session, not a crash. The request
              // goes out unauthenticated and the API answers 401, which the startup
              // gate already knows how to show.
              return undefined;
            })
        : null,
    [user, getAccessToken],
  );

  // Registers the getter with the fetch layer, and only then reports the session as
  // ready — otherwise a child could fire a request before the fetch layer knows about
  // it. Done during render rather than in an effect: an effect runs after commit, which
  // would leave one render where `isAuthenticated` is true but the getter isn't
  // registered yet. React re-renders immediately when state is set during render like
  // this, so it costs an extra render pass but not an extra paint.
  const [registered, setRegistered] = useState<{
    tokenGetter: AuthTokenGetter | null | typeof UNREGISTERED;
    readySessionKey: string | null;
  }>({ tokenGetter: UNREGISTERED, readySessionKey: null });

  if (registered.tokenGetter !== tokenGetter) {
    // Handed over as a function, not a value: AuthKit rotates the token and this is the
    // only way a request an hour from now still carries a live one.
    setAuthTokenGetter(tokenGetter);
    setRegistered({ tokenGetter, readySessionKey: tokenGetter ? sessionKey : null });
  }

  const sessionReady = Boolean(user) && registered.readySessionKey === sessionKey;

  const value = useMemo<PortalAuth>(
    () => ({
      isLoading: auth.isLoading || (Boolean(user) && !sessionReady),
      isAuthenticated: sessionReady,
      sessionKey,
      signIn: () => void signIn(),
      switchAccount: signOutToHome,
      signInAsAdmin: null,
      signOut: signOutToHome,
      email: user?.email ?? null,
    }),
    [auth.isLoading, sessionKey, sessionReady, user, signIn, signOutToHome],
  );

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
}

/**
 * The no-WorkOS path.
 *
 * Reports a session only after a local identity has been chosen. The API still grants
 * the session from development headers; this just makes the choice explicit at startup.
 */
function WithoutWorkOs({ children }: PropsWithChildren) {
  const [identity, setIdentity] = useState(() => readDevIdentity());

  const signIn = useCallback(() => {
    const next = { user: 'partner' as const };
    writeDevIdentity(next);
    setIdentity(next);
  }, []);

  const signInAsAdmin = useCallback(() => {
    const next = { user: 'admin' as const };
    writeDevIdentity(next);
    setIdentity(next);
  }, []);

  const signOut = useCallback(() => {
    writeDevIdentity({});
    setIdentity({});
    window.location.assign('/');
  }, []);

  const sessionKey = [
    'dev',
    identity.user ?? 'none',
    identity.partner ?? '',
    identity.impersonate ?? '',
  ].join(':');

  const value = useMemo<PortalAuth>(
    () => ({
      isLoading: false,
      isAuthenticated: Boolean(identity.user),
      sessionKey,
      signIn,
      switchAccount: signOut,
      signInAsAdmin,
      signOut,
      email: null,
    }),
    [identity.user, sessionKey, signIn, signInAsAdmin, signOut],
  );

  useEffect(() => setAuthTokenGetter(null), []);

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
}

export function PortalAuthProvider({ children }: PropsWithChildren) {
  if (!CLIENT_ID) return <WithoutWorkOs>{children}</WithoutWorkOs>;

  return (
    <AuthKitProvider
      clientId={CLIENT_ID}
      {...(AUTH_API_HOSTNAME ? { apiHostname: AUTH_API_HOSTNAME } : {})}
      redirectUri={window.location.origin}
      onRedirectCallback={({ state }) => {
        // Send the viewer back where they were before the redirect, not to the root.
        const returnTo = (state as { returnTo?: string } | undefined)?.returnTo;
        if (returnTo) window.location.href = returnTo;
      }}
    >
      <AuthKitBridge>{children}</AuthKitBridge>
    </AuthKitProvider>
  );
}

export function usePortalAuth(): PortalAuth {
  const context = useContext(PortalAuthContext);
  if (!context) throw new Error('usePortalAuth must be used within PortalAuthProvider');
  return context;
}
